/**
 * 3CX Call Centre — post-cleanup lean surface.
 *
 * Everything the old page did that relied on browser SIP / JsSIP has
 * been removed (3CX Hosted blocks third-party SIP softphones). The
 * page now speaks exclusively to the supported 3CX APIs:
 *
 *   • **Call Control API** (`/callcontrol/*`) — live participant
 *     listing + hangup (LiveCallsCard).
 *   • **Configuration API** (`/xapi/v1/*`) — recordings, voicemails,
 *     call analytics, users lookup.
 *
 * Audio itself lives in the agent's existing 3CX Web Client tab —
 * we just render a launch button (top-right + banner) so it opens
 * with one click.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Phone, PhoneOff, PhoneIncoming, Loader2, Pencil, RefreshCw,
  CircleCheck, CircleAlert, Voicemail, FileAudio, Mail,
  ChevronDown, ChevronRight, ExternalLink, Users as UsersIcon,
  AlertTriangle, Trash2, BarChart3,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Small helpers                                                     */
/* ------------------------------------------------------------------ */

function webClientUrl(pbxHost) {
  if (!pbxHost) return null;
  // 3CX Hosted Web Client lives at https://<pbx>/webclient/. Strip any
  // protocol prefix + trailing slashes the user may have pasted.
  const host = pbxHost.replace(/^https?:\/\//, "").replace(/\/+$/, "");
  return `https://${host}/webclient/`;
}

function KV({ label, value, mono }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.12em] font-bold text-zinc-500">{label}</div>
      <div className={`text-[13px] mt-0.5 ${mono ? "font-mono" : ""} ${!value ? "text-zinc-300" : "text-zinc-800"}`}>
        {value || "—"}
      </div>
    </div>
  );
}

function formatDuration(s) {
  const n = Number(s || 0);
  if (!Number.isFinite(n) || n <= 0) return "—";
  const m = Math.floor(n / 60);
  const r = Math.floor(n % 60);
  return `${m}:${String(r).padStart(2, "0")}`;
}

/* ================================================================== */
/*  Page shell                                                        */
/* ================================================================== */

export default function ThreeCxCallCentrePage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingConfig, setEditingConfig] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/threecx/call-centre/config");
      setConfig(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load 3CX config");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const cfg = config?.config || {};
  const wcUrl = webClientUrl(cfg.pbx_host);
  const configured = Boolean(cfg.pbx_host);

  return (
    <div className="p-6 max-w-7xl" data-testid="threecx-callcentre-page">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-[#0F8B33] text-white">
          <Phone className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="label-overline">3CX · CALL CENTRE</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">3CX Call Centre</h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Live call monitoring, click-to-dial, recordings and voicemails powered by the Call Control + Configuration APIs. Agent audio runs in the 3CX Web Client.
          </p>
        </div>
        {wcUrl && (
          <a href={wcUrl} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm bg-[#0F8B33] text-white text-sm hover:bg-[#0F8B33]/90"
            data-testid="threecx-open-webclient">
            <ExternalLink className="h-4 w-4" /> Open 3CX Web Client
          </a>
        )}
        <Button onClick={reload} variant="outline" className="rounded-sm" disabled={loading}>
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="rounded-sm border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900 flex items-start gap-2 mb-6"
        data-testid="threecx-callcontrol-banner">
        <CircleCheck className="h-4 w-4 text-sky-700 mt-0.5 shrink-0" />
        <div className="flex-1 text-[12px] leading-relaxed">
          <span className="font-semibold">Call Control API is live.</span>{" "}
          Live participant monitoring, click-to-dial and hangup all run
          through the supported{" "}
          <a href="https://www.3cx.com/docs/call-control-api/"
            className="underline inline-flex items-center gap-0.5" target="_blank" rel="noreferrer">
            3CX Call Control API <ExternalLink className="h-3 w-3" />
          </a>. Place and answer calls in your existing 3CX Web Client
          tab (button above) or on your desk phone / 3CX mobile app.
        </div>
      </div>

      {loading ? (
        <Card className="rounded-sm border-zinc-200"><CardContent className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></CardContent></Card>
      ) : (
        <>
          <Card className="rounded-sm border-zinc-200 mb-4" data-testid="threecx-master-config">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Phone className="h-4 w-4 text-zinc-500" />
                <h2 className="font-display font-bold text-lg">Master configuration</h2>
                <Badge variant="outline"
                  className={`rounded-sm text-[10px] ${
                    configured
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-amber-50 text-amber-700 border-amber-200"
                  }`}>
                  {configured
                    ? <><CircleCheck className="h-3 w-3 inline mr-0.5" />Configured</>
                    : <><CircleAlert className="h-3 w-3 inline mr-0.5" />Not configured</>}
                </Badge>
                <Button size="sm" variant="outline" className="rounded-sm ml-auto"
                  onClick={() => setEditingConfig(true)}
                  data-testid="threecx-config-edit">
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> {configured ? "Edit" : "Configure"}
                </Button>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                <KV label="PBX host" value={cfg.pbx_host} mono />
                <KV label="Default outbound DID" value={cfg.default_outbound_did} />
                <KV label="Web Client" value={wcUrl} mono />
              </div>

              {!configured && (
                <div className="mt-3 p-3 rounded-sm border border-amber-200 bg-amber-50 text-xs text-amber-900">
                  Set your 3CX Hosted host (e.g.{" "}
                  <span className="font-mono">iceberg.3cx.com.au</span>){" "}
                  to enable the Web Client launch button. Everything else
                  runs from the API credentials you saved on the{" "}
                  <a href="/integrations/3cx" className="underline">Phone API</a>{" "}
                  page.
                </div>
              )}
            </CardContent>
          </Card>

          <LiveCallsCard />

          {configured && <CallLogCard />}
          {configured && <ClientExtensionAssignmentsCard />}
          {configured && <RecordingsVoicemailsCard />}
        </>
      )}

      {editingConfig && (
        <ConfigDialog initial={cfg}
          onClose={() => { setEditingConfig(false); reload(); }} />
      )}
    </div>
  );
}

/* ================================================================== */
/*  Master config dialog — now just PBX host + default DID.           */
/* ================================================================== */

function ConfigDialog({ initial, onClose }) {
  const [form, setForm] = useState({
    pbx_host: initial?.pbx_host || "",
    default_outbound_did: initial?.default_outbound_did || "",
    enabled: initial?.enabled ?? true,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/threecx/call-centre/config", form);
      toast.success("3CX configuration saved");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="threecx-config-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-xl">3CX configuration</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-500 -mt-2 mb-2">
          Just two fields. Everything else (auth, roles) lives on the{" "}
          <a href="/integrations/3cx" className="underline">Phone API</a>{" "}
          page.
        </p>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.12em] font-bold text-zinc-600">PBX host</label>
            <Input value={form.pbx_host}
              onChange={(e) => setForm({ ...form, pbx_host: e.target.value })}
              placeholder="iceberg.3cx.com.au"
              className="rounded-sm font-mono text-[13px] mt-1"
              data-testid="threecx-cfg-host" />
            <p className="text-[10px] text-zinc-500 mt-1">
              The domain of your 3CX Hosted PBX. Drives the &quot;Open 3CX Web
              Client&quot; button — the app opens{" "}
              <span className="font-mono">https://&lt;host&gt;/webclient/</span>.
            </p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.12em] font-bold text-zinc-600">Default outbound DID</label>
            <Input value={form.default_outbound_did}
              onChange={(e) => setForm({ ...form, default_outbound_did: e.target.value })}
              placeholder="+61 2 1234 5678"
              className="rounded-sm mt-1" />
            <p className="text-[10px] text-zinc-500 mt-1">
              Optional. Passed to 3CX on outbound calls when a user
              doesn&apos;t have their own DID assigned.
            </p>
          </div>
          <label className="flex items-center gap-1.5 text-sm text-zinc-600 pt-1">
            <input type="checkbox" checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
            Enable call centre system-wide
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-zinc-200 mt-3">
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={saving}
            className="rounded-sm bg-[#0F8B33] hover:bg-[#0F8B33]/90"
            data-testid="threecx-cfg-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================== */
/*  Live Calls panel — Call Control API                               */
/* ================================================================== */

function LiveCallsCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [drops, setDrops] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/threecx/master/active-calls");
      setRows(data?.participants || []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const drop = async (r) => {
    const key = `${r.dn}:${r.participant_id}`;
    setDrops((d) => ({ ...d, [key]: true }));
    try {
      await api.post(
        `/threecx/master/participants/${encodeURIComponent(r.dn)}/${encodeURIComponent(r.participant_id)}/drop`,
      );
      toast.success(`Dropped call on ext ${r.dn}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Drop failed");
    } finally {
      setDrops((d) => { const next = { ...d }; delete next[key]; return next; });
    }
  };

  return (
    <Card className="rounded-sm border-zinc-200 mb-4" data-testid="threecx-live-calls">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <PhoneIncoming className="h-4 w-4 text-zinc-500" />
          <h2 className="font-display font-bold text-lg">Live calls</h2>
          <Badge variant="outline" className="rounded-sm text-[10px] bg-zinc-50 text-zinc-700 border-zinc-200">
            {rows.length} active
          </Badge>
          <label className="ml-auto flex items-center gap-1.5 text-[11px] text-zinc-500 cursor-pointer">
            <input type="checkbox" checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="accent-[#002FA7]"
              data-testid="threecx-live-autorefresh" />
            Auto-refresh (5s)
          </label>
          <Button size="sm" variant="outline" className="rounded-sm"
            onClick={load} disabled={loading}
            data-testid="threecx-live-refresh">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                     : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>

        {error ? (
          <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900"
            data-testid="threecx-live-error">
            {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="text-[12px] italic text-zinc-500 py-4 text-center"
            data-testid="threecx-live-empty">
            No active calls right now. Refreshes every 5&nbsp;seconds while auto-refresh is on.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 border-b">
                  <th className="p-2">Extension</th>
                  <th className="p-2">Other party</th>
                  <th className="p-2">Direction</th>
                  <th className="p-2">Status</th>
                  <th className="p-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const key = `${r.dn}:${r.participant_id}`;
                  return (
                    <tr key={key} className="border-b last:border-b-0"
                      data-testid={`threecx-live-row-${r.participant_id}`}>
                      <td className="p-2 font-mono text-[12px]">{r.dn || "—"}</td>
                      <td className="p-2 text-[12px]">
                        <div className="font-medium">
                          {r.party_caller_name || r.party_caller_id || "—"}
                        </div>
                        {r.party_dn && (
                          <div className="text-[10px] font-mono text-zinc-500">{r.party_dn}</div>
                        )}
                      </td>
                      <td className="p-2 text-[11px]">{r.direction || "—"}</td>
                      <td className="p-2">
                        <Badge variant="outline" className="rounded-sm text-[10px]">
                          {r.status || "—"}
                        </Badge>
                      </td>
                      <td className="p-2 text-right">
                        <Button size="sm" variant="outline"
                          className="rounded-sm text-rose-600 hover:bg-rose-50 h-7 text-[11px]"
                          onClick={() => drop(r)}
                          disabled={drops[key]}
                          data-testid={`threecx-live-drop-${r.participant_id}`}>
                          {drops[key]
                            ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            : <PhoneOff className="h-3 w-3 mr-1" />}
                          Drop
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ================================================================== */
/*  Client Assignments — local overlay                                */
/* ================================================================== */

function ClientExtensionAssignmentsCard() {
  const [extensions, setExtensions] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadError, setReloadError] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setReloadError(null);
    try {
      const [extResp, cliResp] = await Promise.all([
        api.get("/threecx/master/extensions"),
        api.get("/clients").catch(() => ({ data: [] })),
      ]);
      const exts = extResp.data?.extensions || [];
      setExtensions(exts);
      setClients(cliResp.data || []);
      const seed = {};
      for (const e of exts) {
        seed[e.id] = {
          client_id: e.assigned_client_id || "",
          person_name: e.person_name || "",
          routes_to: e.routes_to || "",
          description: e.description || "",
        };
      }
      setDraft(seed);
    } catch (e) {
      setReloadError(e.response?.data?.detail || "Failed to load extensions");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveAsg = async (extId, partial) => {
    const current = draft[extId] || {};
    const next = { ...current, ...partial };
    setDraft((d) => ({ ...d, [extId]: next }));
    setSaving((s) => ({ ...s, [extId]: true }));
    try {
      await api.post(
        `/threecx/master/extensions/${encodeURIComponent(extId)}/assignment`, {
          client_id: next.client_id || null,
          person_name: next.person_name || "",
          routes_to: next.routes_to || "",
          description: next.description || "",
        });
      toast.success("Assignment saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
      const orig = extensions.find((x) => x.id === extId) || {};
      setDraft((d) => ({ ...d, [extId]: {
        client_id: orig.assigned_client_id || "",
        person_name: orig.person_name || "",
        routes_to: orig.routes_to || "",
        description: orig.description || "",
      }}));
    } finally {
      setSaving((s) => { const n = { ...s }; delete n[extId]; return n; });
    }
  };

  const clearAll = async () => {
    if (!window.confirm(
      "Clear every extension assignment (client, person, routes-to, description)?\n\n"
      + "The extension list itself will be re-pulled from 3CX and stay intact — only the local overlays are wiped so you can start fresh.")) return;
    try {
      const { data } = await api.delete("/threecx/master/extension-assignments");
      toast.success(`Cleared ${data.deleted || 0} assignment(s)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clear");
    }
  };

  return (
    <Card className="rounded-sm border-zinc-200 mb-4" data-testid="threecx-asg-card">
      <CardContent className="p-0">
        <div className="px-4 py-2.5 border-b border-zinc-200 bg-zinc-50 flex items-center gap-2">
          <UsersIcon className="h-4 w-4 text-zinc-700" />
          <h2 className="font-display font-bold text-sm">Client assignments</h2>
          <span className="text-[10px] font-mono text-zinc-500">
            {extensions.length} extension(s)
          </span>
          <span className="ml-auto text-[11px] text-zinc-500">
            Tag each extension with the client it serves + who it rings.
          </span>
          <Button size="sm" variant="outline"
            className="rounded-sm h-7 text-[10px] text-red-700 hover:bg-red-50 border-red-200 ml-2"
            onClick={clearAll}
            data-testid="threecx-asg-clear-all">
            <Trash2 className="h-3 w-3 mr-1" />
            Clear all & repopulate
          </Button>
        </div>
        {loading ? (
          <div className="p-6 text-center text-xs text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin inline" />
          </div>
        ) : reloadError ? (
          <div className="p-4 text-[12px] text-red-800 bg-red-50 border-t border-red-100 flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
            <span>{reloadError}</span>
          </div>
        ) : extensions.length === 0 ? (
          <div className="p-6 text-center text-xs italic text-zinc-500">
            No extensions returned from the PBX yet.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {extensions.map((e) => {
              const d = draft[e.id] || {};
              const isSaving = !!saving[e.id];
              return (
                <div key={e.id}
                  className="px-4 py-3 grid grid-cols-1 md:grid-cols-[140px_180px_180px_1fr_120px_24px] gap-3 items-start"
                  data-testid={`threecx-asg-row-${e.id}`}>
                  <div>
                    <div className="font-mono text-sm font-semibold">
                      Ext {e.extension || e.id}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate">{e.name || "—"}</div>
                  </div>
                  <select value={d.client_id || ""}
                    onChange={(ev) => saveAsg(e.id, { client_id: ev.target.value })}
                    className="rounded-sm border border-zinc-300 text-[12px] p-1 h-8"
                    disabled={isSaving}>
                    <option value="">— Unassigned —</option>
                    {clients.map((c) => (
                      <option key={c.client_id} value={c.client_id}>{c.company_name}</option>
                    ))}
                  </select>
                  <Input value={d.person_name || ""} placeholder="Person / desk name"
                    onBlur={(ev) => saveAsg(e.id, { person_name: ev.target.value })}
                    onChange={(ev) => setDraft((s) => ({ ...s, [e.id]: { ...s[e.id], person_name: ev.target.value }}))}
                    className="rounded-sm text-[12px] h-8" />
                  <Input value={d.description || ""} placeholder="Description / notes"
                    onBlur={(ev) => saveAsg(e.id, { description: ev.target.value })}
                    onChange={(ev) => setDraft((s) => ({ ...s, [e.id]: { ...s[e.id], description: ev.target.value }}))}
                    className="rounded-sm text-[12px] h-8" />
                  <Input value={d.routes_to || ""} placeholder="Routes to…"
                    onBlur={(ev) => saveAsg(e.id, { routes_to: ev.target.value })}
                    onChange={(ev) => setDraft((s) => ({ ...s, [e.id]: { ...s[e.id], routes_to: ev.target.value }}))}
                    className="rounded-sm text-[12px] h-8" />
                  <div className="flex items-center h-8">
                    {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ================================================================== */
/*  Call Log — 24h rolling, built from Call Control snapshots         */
/*                                                                    */
/*  The backend polls /callcontrol every 30s and diffs against the    */
/*  previous snapshot. Anything that vanished between polls is        */
/*  written to `threecx_call_log`. This works even when the           */
/*  Configuration API's /Recordings endpoint is 403'd (some 3CX       */
/*  Hosted SKUs paywall recordings behind a separate scope).          */
/* ================================================================== */

function CallLogCard() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [hours, setHours] = useState(24);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [callLog, callStatus] = await Promise.all([
        api.get("/threecx/master/call-log", { params: { hours } }),
        api.get("/threecx/master/call-log/status").catch(() => null),
      ]);
      setData(callLog.data);
      setStatus(callStatus?.data || null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load call log");
    } finally { setLoading(false); }
  }, [hours]);

  useEffect(() => { load(); }, [load]);
  // Auto-refresh every 30s so newly-completed calls appear.
  useEffect(() => {
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const calls = data?.calls || [];
  const kpis = data?.kpis;

  const pollerAge = (() => {
    if (!status?.polled_at) return null;
    const then = new Date(status.polled_at).getTime();
    const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
    if (secs < 60) return `${secs}s ago`;
    return `${Math.round(secs / 60)}m ago`;
  })();

  return (
    <Card className="rounded-sm border-zinc-200 mb-4" data-testid="threecx-call-log">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="h-4 w-4 text-zinc-500" />
          <h2 className="font-display font-bold text-lg">Call log (rolling)</h2>
          <Badge variant="outline"
            className={`rounded-sm text-[10px] ${
              pollerAge && !pollerAge.startsWith("999")
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-zinc-50 text-zinc-600 border-zinc-200"
            }`}
            data-testid="threecx-poller-status">
            Poller · last snapshot {pollerAge || "—"}
          </Badge>
          <select value={hours} onChange={(e) => setHours(Number(e.target.value))}
            className="ml-auto rounded-sm border border-zinc-300 text-[12px] p-1 h-7"
            data-testid="threecx-log-hours">
            <option value={1}>Last hour</option>
            <option value={6}>Last 6h</option>
            <option value={24}>Last 24h</option>
            <option value={72}>Last 3 days</option>
            <option value={168}>Last 7 days</option>
          </select>
          <Button size="sm" variant="outline" className="rounded-sm ml-1"
            onClick={load} disabled={loading}
            data-testid="threecx-log-refresh">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" />
                     : <RefreshCw className="h-3 w-3" />}
          </Button>
        </div>

        <p className="text-[11px] text-zinc-500 mb-3">
          Built from Call Control API snapshots (polled every 30
          seconds). Works even when the Configuration API&apos;s
          Recordings endpoint is blocked. Retains
          the last 7 days of completed calls.
        </p>

        {kpis && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3 pb-3 border-b border-zinc-100">
            <KV label="Total calls" value={kpis.total} />
            <KV label="Inbound" value={kpis.inbound} />
            <KV label="Outbound" value={kpis.outbound} />
            <KV label="Internal" value={kpis.internal} />
            <KV label="Avg duration" value={formatDuration(kpis.average_duration_seconds)} />
            <KV label="Peak hour" value={kpis.peak_hour != null ? `${kpis.peak_hour}:00` : "—"} />
          </div>
        )}

        {error ? (
          <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900">
            {error}
          </div>
        ) : calls.length === 0 ? (
          <div className="text-[12px] italic text-zinc-500 py-4 text-center"
            data-testid="threecx-log-empty">
            No completed calls in the last {hours}&nbsp;hour{hours === 1 ? "" : "s"}.
            The poller starts collecting from the moment the app boots — historical calls that ended before then aren&apos;t
            captured.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 border-b">
                  <th className="p-2">Ended</th>
                  <th className="p-2">Extension</th>
                  <th className="p-2">Other party</th>
                  <th className="p-2">Direction</th>
                  <th className="p-2">Duration</th>
                  <th className="p-2">Final status</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((r) => (
                  <tr key={r.log_id} className="border-b last:border-b-0"
                    data-testid={`threecx-log-row-${r.log_id}`}>
                    <td className="p-2 text-[12px] whitespace-nowrap">
                      {(r.ended_at || "").replace("T", " ").slice(0, 19)}
                    </td>
                    <td className="p-2 text-[12px] font-mono">{r.dn || "—"}</td>
                    <td className="p-2 text-[12px]">
                      <div className="font-medium">
                        {r.party_caller_name || r.party_caller_id || "—"}
                      </div>
                      {r.party_dn && (
                        <div className="text-[10px] font-mono text-zinc-500">{r.party_dn}</div>
                      )}
                    </td>
                    <td className="p-2 text-[11px]">{r.direction || "—"}</td>
                    <td className="p-2 text-[12px]">{formatDuration(r.duration_seconds)}</td>
                    <td className="p-2">
                      <Badge variant="outline" className="rounded-sm text-[10px]">
                        {r.final_status || "—"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ================================================================== */
/*  Recordings + Voicemails                                           */
/* ================================================================== */

function RecordingsVoicemailsCard() {
  const [tab, setTab] = useState("recordings");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [voiceCount, setVoiceCount] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = tab === "recordings"
        ? "/threecx/master/recordings"
        : "/threecx/master/voicemails";
      const { data } = await api.get(url, { params: { limit: 100 } });
      setRows(data?.items || []);
      if (tab === "voicemails") setVoiceCount(data?.unread_count ?? null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load");
      setRows([]);
    } finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card className="rounded-sm border-zinc-200 mb-4" data-testid="threecx-rec-vm-card">
      <CardContent className="p-0">
        <div className="px-4 py-2.5 border-b border-zinc-200 bg-zinc-50 flex items-center gap-2">
          <FileAudio className="h-4 w-4 text-zinc-700" />
          <h2 className="font-display font-bold text-sm">Call recordings & voicemails</h2>
          <div className="ml-auto flex gap-1">
            <TabBtn active={tab === "recordings"} onClick={() => setTab("recordings")} testid="threecx-tab-recordings">
              <FileAudio className="h-3 w-3 mr-1" /> Recordings
            </TabBtn>
            <TabBtn active={tab === "voicemails"} onClick={() => setTab("voicemails")} testid="threecx-tab-voicemails">
              <Voicemail className="h-3 w-3 mr-1" /> Voicemails
              {voiceCount != null && voiceCount > 0 && (
                <Badge variant="outline" className="ml-1 rounded-sm text-[9px] bg-red-50 text-red-700 border-red-200">
                  {voiceCount}
                </Badge>
              )}
            </TabBtn>
            <Button size="sm" variant="outline" className="rounded-sm ml-1" onClick={load} disabled={loading}>
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            </Button>
          </div>
        </div>
        {loading ? (
          <div className="p-6 text-center text-xs text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin inline" />
          </div>
        ) : error ? (
          <div className="p-3 text-[12px] text-amber-900 bg-amber-50 border-b border-amber-200">
            {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-xs italic text-zinc-500">
            Nothing to show.
          </div>
        ) : tab === "recordings" ? (
          <RecordingsTable rows={rows} />
        ) : (
          <VoicemailsTable rows={rows} onChanged={load} />
        )}
      </CardContent>
    </Card>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center h-7 px-2 rounded-sm text-[11px] font-medium ${
        active
          ? "bg-[#0F8B33] text-white"
          : "bg-white text-zinc-700 border border-zinc-200 hover:bg-zinc-50"
      }`}>
      {children}
    </button>
  );
}

function RecordingsTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 border-b">
            <th className="p-2">When</th>
            <th className="p-2">Direction</th>
            <th className="p-2">From</th>
            <th className="p-2">To</th>
            <th className="p-2">Duration</th>
            <th className="p-2 text-right">Recording</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b last:border-b-0" data-testid={`threecx-rec-row-${r.id}`}>
              <td className="p-2 text-[12px]">{(r.call_time || "").replace("T", " ").slice(0, 19)}</td>
              <td className="p-2 text-[12px]">{r.call_type || "—"}</td>
              <td className="p-2 text-[12px] font-mono">{r.caller || "—"}</td>
              <td className="p-2 text-[12px] font-mono">{r.callee || "—"}</td>
              <td className="p-2 text-[12px]">{formatDuration(r.duration_seconds)}</td>
              <td className="p-2 text-right">
                {r.url ? (
                  <a href={r.url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border border-zinc-200 text-[11px] hover:bg-zinc-50">
                    <ExternalLink className="h-3 w-3" /> Open
                  </a>
                ) : <span className="text-zinc-300 text-[11px]">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VoicemailsTable({ rows, onChanged }) {
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState({});

  const setRead = async (id, read) => {
    setBusy((s) => ({ ...s, [id]: true }));
    try {
      await api.patch(`/threecx/master/voicemails/${encodeURIComponent(id)}`, { read });
      toast.success(read ? "Marked read" : "Marked unread");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy((s) => { const n = { ...s }; delete n[id]; return n; });
    }
  };

  return (
    <div className="divide-y divide-zinc-100">
      {rows.map((v) => {
        const open = !!expanded[v.id];
        return (
          <div key={v.id} data-testid={`threecx-vm-row-${v.id}`}>
            <div className="px-4 py-2.5 grid grid-cols-[24px_140px_1fr_130px_140px_120px] gap-2 items-center text-[12px] hover:bg-zinc-50">
              <button onClick={() => setExpanded((e) => ({ ...e, [v.id]: !open }))}
                className="text-zinc-400 hover:text-zinc-700">
                {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              </button>
              <span className="font-mono">{(v.received_time || "").replace("T", " ").slice(0, 16)}</span>
              <span className="font-mono">{v.caller || "—"} → ext {v.extension || "—"}</span>
              <span>{formatDuration(v.duration_seconds)}</span>
              <Badge variant="outline" className={`rounded-sm text-[10px] w-fit ${
                v.read ? "bg-zinc-50 text-zinc-600 border-zinc-200" : "bg-red-50 text-red-700 border-red-200"
              }`}>
                {v.read ? "Read" : "Unread"}
              </Badge>
              <div className="flex items-center gap-1 justify-end">
                <Button size="sm" variant="outline" className="rounded-sm h-6 text-[10px]"
                  onClick={() => setRead(v.id, !v.read)} disabled={!!busy[v.id]}>
                  <Mail className="h-3 w-3 mr-1" /> {v.read ? "Mark unread" : "Mark read"}
                </Button>
              </div>
            </div>
            {open && (
              <div className="px-8 py-3 bg-zinc-50/60 border-t border-zinc-100 text-[12px] space-y-1">
                <KV label="Subject" value={v.subject} />
                <KV label="Transcript" value={v.transcript} />
                {v.url && (
                  <a href={v.url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border border-zinc-200 text-[11px] hover:bg-zinc-50 mt-1">
                    <ExternalLink className="h-3 w-3" /> Open in 3CX
                  </a>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
